/**
 * Auth feature: wire login panel and show/hide chat/notes content based on user.
 */

import type { AuthService, DrawerView } from '../core/types';

export interface AuthActions {
  signInGoogle?: () => void | Promise<void>;
  signInGithub?: () => void | Promise<void>;
}

export function registerAuth(
  authService: AuthService,
  drawerView: DrawerView,
  authActions: AuthActions
): void {
  function showLoginError(message: string): void {
    const el = drawerView.getChatLoginError?.();
    if (el) {
      el.textContent = message || 'Sign-in failed';
      el.classList.remove('rf-hidden');
    }
  }

  function updateChatAuthUI(): void {
    const authReady = !authService.isAuthReady || authService.isAuthReady();
    const authLoading = drawerView.getChatAuthLoading?.();
    const loginPanel = drawerView.getChatLoginPanel?.();
    const chatPanel = drawerView.getChatPanel?.();
    const notesSignin = drawerView.getNotesSigninMsg?.();
    const notesEditorWrap = drawerView.getNotesEditorWrap?.();
    const loginError = drawerView.getChatLoginError?.();

    // When auth not ready (e.g. Firebase loading), show loading and hide login/chat until first state. Matches plugin/lectura auth.js updateChatAuthUI.
    if (!authReady) {
      if (authLoading) authLoading.classList.remove('rf-hidden');
      if (loginPanel) loginPanel.classList.add('rf-hidden');
      if (chatPanel) chatPanel.classList.add('rf-hidden');
      return;
    }

    if (authLoading) authLoading.classList.add('rf-hidden');
    const user = authService.getCurrentUser();
    const isRealUser = user && !user.isAnonymous;
    if (loginPanel) loginPanel.classList.toggle('rf-hidden', !!isRealUser);
    if (chatPanel) chatPanel.classList.toggle('rf-hidden', !isRealUser);
    if (notesSignin) notesSignin.classList.toggle('rf-hidden', !!isRealUser);
    if (notesEditorWrap) notesEditorWrap.classList.toggle('rf-hidden', !isRealUser);
    if (loginError) {
      if (isRealUser) {
        loginError.textContent = '';
        loginError.classList.add('rf-hidden');
      }
    }
  }

  function handleSignIn(fn: (() => void | Promise<void>) | undefined): void {
    if (typeof fn !== 'function') return;
    const p = Promise.resolve(fn());
    p.then(updateChatAuthUI).catch((err: Error) => showLoginError(err?.message || 'Sign-in failed'));
  }

  const loginPanel = drawerView.getChatLoginPanel?.();
  if (loginPanel) {
    const googleBtn = loginPanel.querySelector('.rf-chat-login-google');
    const githubBtn = loginPanel.querySelector('.rf-chat-login-github');
    if (googleBtn && authActions.signInGoogle) googleBtn.addEventListener('click', () => handleSignIn(authActions.signInGoogle));
    if (githubBtn && authActions.signInGithub) githubBtn.addEventListener('click', () => handleSignIn(authActions.signInGithub));
  }
  const notesSignin = drawerView.getNotesSigninMsg?.();
  if (notesSignin) {
    const googleBtn = notesSignin.querySelector('.rf-chat-login-google');
    const githubBtn = notesSignin.querySelector('.rf-chat-login-github');
    if (googleBtn && authActions.signInGoogle) googleBtn.addEventListener('click', () => handleSignIn(authActions.signInGoogle));
    if (githubBtn && authActions.signInGithub) githubBtn.addEventListener('click', () => handleSignIn(authActions.signInGithub));
  }

  if (authService.subscribeUserChange) {
    authService.subscribeUserChange(updateChatAuthUI);
  }
  updateChatAuthUI();
}
